import React, { useState } from "react";
import {
  Building2,
  Phone,
  Banknote,
  Settings2,
  FileText,
  BadgeCheck,
  Globe,
  Package,
  FileArchive,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useVendorContext } from "../../context/VendorContext";

const ProfilePage = () => {
  const { vendor } = useVendorContext();
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  if (!vendor) return <div className="p-6 text-red-600">Vendor data not available.</div>;

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isImageFile = (url: string) =>
    /\.(jpeg|jpg|png|webp|gif)$/i.test(url);

  const sectionClass = "bg-white shadow rounded-lg mb-6 overflow-hidden border border-gray-200";
  const labelClass = "text-gray-500 text-xs uppercase mb-1";
  const valueClass = "text-gray-800 text-sm";

  const renderRow = (label: string, value?: string | number | null) => (
    <div>
      <p className={labelClass}>{label}</p>
      <p className={valueClass}>{value || "-"}</p>
    </div>
  );

  const renderDocument = (label: string, url?: string | null) => {
    if (!url) return null;
    return (
      <div>
        <p className={labelClass}>{label}</p>
        {isImageFile(url) ? (
          <img
            src={url}
            alt={label}
            className="h-32 rounded-md border object-contain mb-2"
          />
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm underline">
            View {label}
          </a>
        )}
      </div>
    );
  };

  const Section = ({
    title,
    icon: Icon,
    id,
    children,
  }: {
    title: string;
    icon: React.ElementType;
    id: string;
    children: React.ReactNode;
  }) => {
    const isCollapsed = collapsedSections[id];

    return (
      <div className={sectionClass}>
        <button
          onClick={() => toggleSection(id)}
          className="w-full px-5 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-yellow-500" />
            <h2 className="text-md font-semibold">{title}</h2>
          </div>
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>
        {!isCollapsed && <div className="px-5 py-4 grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">Vendor Profile</h1>

      <Section title="Business Info" icon={Building2} id="business">
        {renderRow("Vendor Name", vendor.vendorName)}
        {renderRow("Business Type", vendor.businessInfo?.businessType)}
        {renderRow("GST Number", vendor.businessInfo?.gstNumber)}
        {renderRow("PAN Number", vendor.businessInfo?.panNumber)}
        {renderRow("Address", vendor.businessInfo?.address)}
        {renderRow("Operating Locations", vendor.businessInfo?.operatingLocations)}
        {renderRow("Years in Business", vendor.businessInfo?.yearsInBusiness)}
      </Section>

      <Section title="Contact Info" icon={Phone} id="contact">
        {renderRow("Contact Person", vendor.contactInfo?.contactPerson)}
        {renderRow("Designation", vendor.contactInfo?.designation)}
        {renderRow("Phone Number", vendor.contactInfo?.phoneNumber)}
        {renderRow("WhatsApp Number", vendor.contactInfo?.whatsappNumber)}
        {renderRow("Email", vendor.contactInfo?.email)}
      </Section>

      <Section title="Bank Details" icon={Banknote} id="bank">
        {renderRow("Account Name", vendor.bankDetails?.bankAccountName)}
        {renderRow("Account Number", vendor.bankDetails?.bankAccountNumber)}
        {renderRow("Bank Name", vendor.bankDetails?.bankName)}
        {renderRow("Bank Branch", vendor.bankDetails?.bankBranch)}
        {renderRow("IFSC Code", vendor.bankDetails?.ifscCode)}
      </Section>

      <Section title="Capabilities" icon={Settings2} id="capabilities">
        {renderRow("Jewelry Categories", vendor.capabilities?.jewelryCategories?.join(", "))}
        {renderRow("Metals Used", vendor.capabilities?.metalsUsed)}
        {renderRow("Gemstones Offered", vendor.capabilities?.gemstonesOffered)}
        {renderRow("Customization Supported", vendor.capabilities?.supportsCustomization ? "Yes" : "No")}
        {renderRow("Average Lead Time", vendor.capabilities?.averageLeadTime)}
      </Section>

      <Section title="Pricing & Policies" icon={FileText} id="pricing">
        {renderRow("Pricing Structure", vendor.pricingPolicies?.pricingStructure)}
        {renderRow("Discount Policy", vendor.pricingPolicies?.discountPolicy)}
        {renderRow("Return Policy", vendor.pricingPolicies?.returnPolicy)}
        {renderRow("Shipping Policy", vendor.pricingPolicies?.shippingPolicy)}
        {renderRow("Payment Terms", vendor.pricingPolicies?.paymentTerms)}
      </Section>

      <Section title="Certifications" icon={BadgeCheck} id="certifications">
        {renderRow("Diamond Certification", vendor.certifications?.hasDiamondCertification ? "Yes" : "No")}
        {renderRow("Hallmark Certification", vendor.certifications?.hasHallmarkCertification ? "Yes" : "No")}
        {renderRow("Certification Bodies", vendor.certifications?.certificationBodies)}
        {renderRow("Authorized for Branded Items", vendor.certifications?.isAuthorizedForBrandedItems ? "Yes" : "No")}
      </Section>

      <Section title="Digital Presence" icon={Globe} id="digital">
        {renderRow("Website", vendor.digitalPresence?.websiteUrl)}
        {renderRow("Facebook", vendor.digitalPresence?.facebookUrl)}
        {renderRow("Instagram", vendor.digitalPresence?.instagramUrl)}
        {renderRow("Twitter", vendor.digitalPresence?.twitterUrl)}
        {renderRow("LinkedIn", vendor.digitalPresence?.linkedInUrl)}
        {renderRow("Other Platforms", vendor.digitalPresence?.otherPlatformUrl)}
        {renderRow("Catalog URL", vendor.digitalPresence?.catalogUrl)}
        {renderRow("Marketplace Presence", vendor.digitalPresence?.marketplacePresence)}
      </Section>

      <Section title="Operational Preferences" icon={Package} id="operational">
        {renderRow("Communication Mode", vendor.operationalPreferences?.preferredCommunicationMode)}
        {renderRow("Order Handling Process", vendor.operationalPreferences?.orderHandlingProcess)}
        {renderRow("Inventory Update Frequency", vendor.operationalPreferences?.inventoryUpdateFrequency)}
        {renderRow("Packaging Standards", vendor.operationalPreferences?.packagingStandards)}
      </Section>

      <Section title="Documents" icon={FileArchive} id="documents">
        {renderDocument("GST Certificate", vendor.documentReferences?.gstCertificatePath)}
        {renderDocument("PAN Card", vendor.documentReferences?.panCardPath)}
        {renderDocument("Bank Cheque", vendor.documentReferences?.bankChequePath)}
        {renderDocument("Address Proof", vendor.documentReferences?.addressProofPath)}
        {renderDocument("Certification Docs", vendor.documentReferences?.certificationDocsPath)}
        {renderDocument("BIS Hallmark Certificate", vendor.documentReferences?.bisHallmarkCertificatePath)}
        {renderDocument("GIA Certificate", vendor.documentReferences?.giaCertificatePath)}
      </Section>
    </div>
  );
};

export default ProfilePage;
